import { Apple, Chrome, Facebook, Twitter, Github, Linkedin } from 'lucide-react';
import type { SocialProviderConfig, SocialProvider } from '@/services/interfaces/Auth';

// 社交登录提供者图标映射
export const SocialProviderIcons = {
  apple: Apple,
  'google-oauth2': Chrome,
  facebook: Facebook,
  twitter: Twitter,
  microsoft: Chrome, // 使用Chrome图标作为Microsoft的临时图标
  github: Github,
  linkedin: Linkedin,
  weibo: Chrome, // 使用Chrome图标作为微博的临时图标
} as const;

// 社交登录提供者颜色映射
export const SocialProviderColors = {
  apple: '#000000',
  'google-oauth2': '#4285F4',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  microsoft: '#00A4EF',
  github: '#333333',
  linkedin: '#0A66C2',
  weibo: '#E6162D',
} as const;

// 默认社交登录提供者配置 - 只启用 Apple 和 Google
export const defaultSocialProviders: SocialProviderConfig[] = [
  {
    id: 'apple',
    name: 'Apple',
    connection: 'apple',
    icon: SocialProviderIcons.apple,
    color: SocialProviderColors.apple,
    enabled: true,
  },
  {
    id: 'google-oauth2',
    name: 'Google',
    connection: 'google-oauth2',
    icon: SocialProviderIcons['google-oauth2'],
    color: SocialProviderColors['google-oauth2'],
    enabled: true,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    connection: 'facebook',
    icon: SocialProviderIcons.facebook,
    color: SocialProviderColors.facebook,
    enabled: false, // 默认禁用
  },
  {
    id: 'twitter',
    name: 'Twitter',
    connection: 'twitter',
    icon: SocialProviderIcons.twitter,
    color: SocialProviderColors.twitter,
    enabled: false, // 默认禁用
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    connection: 'Microsoft',
    icon: SocialProviderIcons.microsoft,
    color: SocialProviderColors.microsoft,
    enabled: false, // 默认禁用
  },
  {
    id: 'github',
    name: 'GitHub',
    connection: 'github',
    icon: SocialProviderIcons.github,
    color: SocialProviderColors.github,
    enabled: false, // 默认禁用
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    connection: 'linkedin',
    icon: SocialProviderIcons.linkedin,
    color: SocialProviderColors.linkedin,
    enabled: false, // 默认禁用
  },
  {
    id: 'weibo',
    name: 'Weibo',
    connection: 'weibo',
    icon: SocialProviderIcons.weibo,
    color: SocialProviderColors.weibo,
    enabled: false, // 默认禁用
  },
];

// 获取启用的社交登录提供者
export const getEnabledProviders = (providers: SocialProviderConfig[] = defaultSocialProviders): SocialProviderConfig[] => {
  return providers.filter(provider => provider.enabled);
};

// 根据ID获取提供者配置
export const getProviderById = (id: SocialProvider, providers: SocialProviderConfig[] = defaultSocialProviders): SocialProviderConfig | undefined => {
  return providers.find(provider => provider.id === id);
};

// 创建自定义提供者配置
export const createProviderConfig = (
  id: SocialProvider,
  overrides: Partial<SocialProviderConfig> = {}
): SocialProviderConfig => {
  const defaultProvider = getProviderById(id);
  if (!defaultProvider) {
    throw new Error(`Unknown social provider: ${id}`);
  }
  
  return {
    ...defaultProvider,
    ...overrides,
  };
};
